import { type ReactNode, useCallback } from 'react';

import { CORE_PAGES } from '@core-ui/components';
import { FeatureProtectedView } from '@moniebook-web/components/molecules/feature-protected-view';
import { ContractRightLine } from '@moniebook-web/icons/arrows/contract-right-line';
import { HotelLine } from '@moniebook-web/icons/buildings/hotel-line';
import { File3Line } from '@moniebook-web/icons/document/file-3-line';
import { AddLine } from '@moniebook-web/icons/system/add-line';
import { QuestionFill } from '@moniebook-web/icons/system/question-fill';
import { Settings2Line } from '@moniebook-web/icons/system/settings-2-line';
import { PAGES } from '@moniebook-web/types';
import {
    baseConfig,
    logout,
    useCustomerAnalytics,
    useUserInfo,
} from '@moniebook-web/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Link, type LinkProps, useNavigate } from 'react-router';

import { useSessionTokenPayload } from '@/features/ghost-session/context/session-token-payload-context';
import { ANALYTIC_EVENTS, BRAZE_EVENTS } from '@/shared/constants';

interface DropdownContentProps {
    onClose: () => void;
    onSwitchBusiness: () => void;
}

const ICON_CLASS = 'size-16 text-new-neutral-700 shrink-0';

export function DropdownContent({
    onClose,
    onSwitchBusiness,
}: Readonly<DropdownContentProps>) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { sendBrazeEvent, sendMixpanelEvent } = useCustomerAnalytics();
    const { currentBusiness, canCreateBusiness } = useUserInfo(true);
    const { isGhostSession } = useSessionTokenPayload();

    const handleSwitchBusiness = useCallback(() => {
        sendMixpanelEvent({ eventName: 'click_switch_business' });
        onClose();
        onSwitchBusiness();
    }, [sendMixpanelEvent, onClose, onSwitchBusiness]);

    const handleCreateBusiness = useCallback(() => {
        onClose();
        sendMixpanelEvent({
            eventName: ANALYTIC_EVENTS.VIEW_CREATE_BUSINESS_FORM,
        });
        sendBrazeEvent({ eventName: BRAZE_EVENTS.CREATE_BUSINESS_CLICKED });
    }, [sendBrazeEvent, sendMixpanelEvent, onClose]);

    return (
        <>
            <button
                type='button'
                onClick={isGhostSession ? undefined : handleSwitchBusiness}
                className='flex h-64 w-full cursor-pointer items-center gap-12 border-new-alpha-150 border-b pr-12 pl-16 outline-none transition-all hover:bg-background-inset-default'>
                <div className='flex size-32 shrink-0 items-center justify-center rounded-full bg-content-heading-default'>
                    <span className='font-medium text-base text-white leading-none'>
                        {currentBusiness?.name.charAt(0).toUpperCase()}
                    </span>
                </div>
                <div className='flex flex-1 flex-col gap-4'>
                    <span className='truncate text-left font-normal text-14 text-new-neutral-1000 leading-none'>
                        {currentBusiness?.name}
                    </span>
                    <span className='truncate text-left font-normal text-14 text-new-neutral-400 leading-none'>
                        {currentBusiness?.roleName}
                    </span>
                </div>
            </button>

            {!isGhostSession && (
                <div className='flex flex-col border-new-alpha-150 border-b p-4'>
                    <NavLinkItem
                        type='button'
                        onClick={handleSwitchBusiness}
                        title='Switch business'
                        icon={<HotelLine className={ICON_CLASS} />}
                    />
                    <FeatureProtectedView
                        flag='moniebook.pos.enableNewBusinessCreation'
                        noAltView>
                        {canCreateBusiness && (
                            <NavLinkItem
                                to={CORE_PAGES.ONBOARDING.BUSINESS_DETAILS}
                                onClick={handleCreateBusiness}
                                icon={<AddLine className={ICON_CLASS} />}
                                title='Add business'
                            />
                        )}
                    </FeatureProtectedView>
                </div>
            )}

            <div className='flex flex-col border-new-alpha-150 border-b p-4'>
                <NavLinkItem
                    to={PAGES.SETTINGS}
                    icon={<Settings2Line className={ICON_CLASS} />}
                    title='Settings'
                    onClick={onClose}
                />
                <NavLinkItem
                    to={baseConfig.MONIEBOOK_CUSTOMER_HELP_URL}
                    target='_blank'
                    icon={<QuestionFill className={ICON_CLASS} />}
                    title='Support'
                    onClick={onClose}
                />
                <NavLinkItem
                    to={PAGES.TERMS}
                    icon={<File3Line className={ICON_CLASS} />}
                    title='Terms and Conditions'
                    onClick={onClose}
                />
                <NavLinkItem
                    to={PAGES.PRIVACY_POLICY}
                    icon={<File3Line className={ICON_CLASS} />}
                    title='Privacy Policy'
                    onClick={onClose}
                />
            </div>

            <div className='flex flex-col p-4'>
                <NavLinkItem
                    to={PAGES.LOGOUT}
                    onClick={logout(navigate, queryClient)}
                    icon={<ContractRightLine className={ICON_CLASS} />}
                    title='Log out'
                />
            </div>
        </>
    );
}

function NavLinkItem({
    to,
    icon,
    title,
    onClick,
    ...props
}: Readonly<
    Omit<LinkProps, 'to'> & {
        to?: string;
        onClick?: VoidFunction;
        icon: ReactNode;
        title: string;
    }
>) {
    const ACTION_ITEM_CLASS =
        'flex items-center h-32 pl-12 pr-8 gap-10 rounded-[6px] text-14 text-new-neutral-1000 font-normal w-full hover:bg-background-inset-default transition-all outline-none cursor-pointer';

    if (!to) {
        return (
            <button
                type='button'
                onClick={onClick}
                className={ACTION_ITEM_CLASS}>
                {icon}
                {title}
            </button>
        );
    }

    return (
        <Link
            {...props}
            to={to}
            onClick={onClick}
            className={ACTION_ITEM_CLASS}>
            {icon} <span>{title}</span>
        </Link>
    );
}
